/**
 * adi-agent — ADI's own identity as the AUTHORISED RECIPIENT on every application.
 *
 * WHY THIS EXISTS
 * ADI is the registered migration agent on every matter in this portal. So the
 * Department's "do you authorise another person to receive written correspondence
 * on your behalf?" question (Immi F0070) has a KNOWN answer before the client ever
 * logs in: yes, a registered migration agent, and that agent is ADI.
 *
 * Asking the client was worse than useless. It let them reach states the Department
 * will not accept (naming a migration agent while declaring they authorise nobody),
 * and it blocked submit on a question they cannot meaningfully answer. These
 * sections are now `client_hidden` and seeded from here when the intake form is
 * created, so the answers are already present for staff to transcribe into
 * ImmiAccount.
 *
 * SOURCE OF TRUTH: adi-immigration/compliance/MARA-CONTACT-BLOCK.md (verified against
 * the OMARA register). If Eli's registration details change, change them THERE, then
 * here, then re-run the backfill (scripts/backfill-agent-details.mjs).
 *
 * The office address is the OMARA-registered business address. The MARA block flags it
 * as "not in the website footer, confirm with Eli before public use" — that caveat is
 * about MARKETING. This is the agent's business address on a Departmental form, which
 * is exactly where it belongs.
 */

import type { AddressValue } from './generated/primitive-types';
import { statusForVariant } from '../lib/relationshipBasis';

export const ADI_AGENT = {
  marn: '1465541',
  familyName: 'Bekker',
  givenNames: 'Ilya Eli',
  title: 'Mr',
  organisation: 'ADI Immigration',
  legalEntity: 'Partner Visa Australia Pty Ltd',
  abn: '97 670 826 892',
  email: 'adi@adibusinessgroup.com',
  // Phones in Immi entry format: digits only, country-code prefixed, no spaces,
  // no '+' — exactly what Eli types into ImmiAccount (confirmed against his
  // live entries, 2026-07-12). ImmiAccount practice reuses the mobile for the
  // home / after-hours slots.
  phoneBusiness: '61390288070',
  phoneMobile: '61444512771',
  phoneHome: '61444512771',
  phoneAfterHours: '61444512771',
  // OMARA-registered business address — identity contexts only. All
  // CORRESPONDENCE goes to the PO Box (postalAddress), matching where ADI
  // actually receives Departmental mail.
  address: {
    line1: '204-206 Balaclava Road',
    suburb: 'Caulfield North',
    state: 'VIC',
    postcode: '3161',
    country: 'AU',
  } satisfies AddressValue,
  postalAddress: {
    line1: 'PO Box 47',
    suburb: 'St Kilda',
    state: 'VIC',
    postcode: '3182',
    country: 'AU',
  } satisfies AddressValue,
} as const;

/**
 * WITNESS CONTACT CHANNELS (R6, Jane 2026-07-21).
 *
 * The witness phone/email fields (applicant.witnesses[*].phone_*, .email) are
 * client_hidden: the client only tells us who their witnesses are and how they
 * know them, and ADI seeds its OWN contact details so the lodged form is
 * complete. Staff can overwrite with a real witness email before the Form 888
 * email is sent (the send-form-888 guard checks the email still equals this
 * seed and refuses to send until a real one is entered).
 *
 * Only the relative keys each form actually carries are returned, so we never
 * write an orphan row: 300 (PMV) has only mobile + email, while 820 and 309
 * also have home + business phone.
 */
const WITNESS_CONTACT_FORMS = new Set([
  '47SP_820_onshore_stage1',
  '47SP_309_offshore_stage1',
  '47SP_300_PMV',
]);

export function witnessContactDefaults(formId: string): Record<string, string> {
  if (!WITNESS_CONTACT_FORMS.has(formId)) return {};
  const out: Record<string, string> = {
    phone_mobile: ADI_AGENT.phoneMobile,
    email: ADI_AGENT.email,
  };
  if (formId === '47SP_820_onshore_stage1' || formId === '47SP_309_offshore_stage1') {
    out.phone_home = ADI_AGENT.phoneHome;
    out.phone_business = ADI_AGENT.phoneBusiness;
  }
  return out;
}

/** canonical_name → value. Written into intake_field_data when a form is created. */
export type AgentPrefill = Record<string, unknown>;

/**
 * The 47SP family (820 / 309 / 300 / stage-2) models the recipient under
 * `application.authorised_recipient.*` with lowercase enum codes.
 *
 * Deliberately NOT set: `lpn` (only appears when the recipient is a legal
 * practitioner) and `type_other_detail` (only when type is "other"). Their
 * appears_when gates are false for a migration agent, so filling them would put
 * data on the form that the Department never asked for.
 *
 * The four 47SP forms do NOT carry the same recipient fields: 300 and stage-2 have
 * no `type` field at all, and 300 has no phone fields. Writing a key a form does not
 * have would leave an orphan intake_field_data row that nothing ever renders, so the
 * maps are built per form and the guard test asserts every key exists on its form.
 */
const PREFILL_47SP_BASE: AgentPrefill = {
  'application.authorised_recipient.is_authorised': 'yes_migration_agent',
  'application.authorised_recipient.provides_immigration_assistance': 'Yes',
  'application.authorised_recipient.marn': ADI_AGENT.marn,
  'application.authorised_recipient.family_name': ADI_AGENT.familyName,
  'application.authorised_recipient.given_names': ADI_AGENT.givenNames,
  'application.authorised_recipient.organisation': ADI_AGENT.organisation,
  'application.authorised_recipient.postal_address': ADI_AGENT.postalAddress,
  'application.authorised_recipient.email': ADI_AGENT.email,
};

const PREFILL_47SP_PHONES: AgentPrefill = {
  'application.authorised_recipient.business_phone': ADI_AGENT.phoneBusiness,
  'application.authorised_recipient.mobile_phone': ADI_AGENT.phoneMobile,
};

/**
 * CLIENT CONTACT CHANNELS — substituted with ADI's own (Tzvi/Eli, 2026-07-12).
 *
 * ADI represents every client, so the Department's correspondence channels
 * (postal address, contact phones, email) are ADI's, not the client's. The
 * client's RESIDENTIAL address stays client-entered and client-visible — it is
 * a factual record, not a correspondence channel. The postal-same-as-residential
 * toggles are pinned to "No" so the postal address block is live and carries
 * the PO Box.
 */
const CONTACT_47SP_STAGE1: AgentPrefill = {
  'applicant.postal_same_as_residential': 'No',
  'applicant.postal_address': ADI_AGENT.postalAddress,
  'applicant.phone_home': ADI_AGENT.phoneHome,
  'applicant.phone_business': ADI_AGENT.phoneBusiness,
  'applicant.phone_mobile': ADI_AGENT.phoneMobile,
  'applicant.email': ADI_AGENT.email,
  'applicant.stage2_contact.postal_address': ADI_AGENT.postalAddress,
  'applicant.stage2_contact.email': ADI_AGENT.email,
};

/** stage-2 has no postal block and only a mobile. */
const CONTACT_47SP_STAGE2: AgentPrefill = {
  'applicant.phone_mobile': ADI_AGENT.phoneMobile,
  'applicant.email': ADI_AGENT.email,
};

const CONTACT_600_APPLICANT: AgentPrefill = {
  'applicant.is_postal_same_as_residential': 'No',
  'applicant.postal_address': ADI_AGENT.postalAddress,
  'applicant.phone_home': ADI_AGENT.phoneHome,
  'applicant.phone_business': ADI_AGENT.phoneBusiness,
  'applicant.phone_mobile': ADI_AGENT.phoneMobile,
  'applicant.agrees_to_email_communication': 'Yes',
  'applicant.email': ADI_AGENT.email,
};

/** 1418 also collects the sponsor's contact channels on the applicant's form. */
const CONTACT_1418_SPONSOR_BLOCK: AgentPrefill = {
  'sponsor.phone_home': ADI_AGENT.phoneHome,
  'sponsor.phone_business': ADI_AGENT.phoneBusiness,
  'sponsor.phone_mobile': ADI_AGENT.phoneMobile,
  'sponsor.email': ADI_AGENT.email,
};

const CONTACT_1006: AgentPrefill = {
  'applicant.is_postal_same_as_residential': 'No',
  'applicant.postal_address': ADI_AGENT.postalAddress,
  'applicant.phone_business': ADI_AGENT.phoneBusiness,
  'applicant.phone_after_hours': ADI_AGENT.phoneAfterHours,
  'applicant.phone_mobile': ADI_AGENT.phoneMobile,
  'applicant.agrees_to_email_communication': 'Yes',
  'applicant.email': ADI_AGENT.email,
  // Only surfaces (form + PDF) when additional applicants exist; correspondence
  // still goes to ADI for all of them.
  'application.correspondence_address_covers_other_applicants': 'Yes',
};

const CONTACT_40SP: AgentPrefill = {
  'sponsor.correspondence_address_same_as_residential': 'No',
  'sponsor.correspondence_address': ADI_AGENT.postalAddress,
  'sponsor.phone_office_hours': ADI_AGENT.phoneBusiness,
  'sponsor.phone_after_hours': ADI_AGENT.phoneAfterHours,
  'sponsor.mobile': ADI_AGENT.phoneMobile,
  'sponsor.consents_to_electronic_communication': 'Yes',
  'sponsor.email': ADI_AGENT.email,
  // sponsor.fax deliberately unseeded — ADI has no fax; the field is optional.
};

const CONTACT_1149: AgentPrefill = {
  'sponsor.correspondence_address_same_as_residential': 'No',
  'sponsor.correspondence_address': ADI_AGENT.postalAddress,
  'sponsor.mobile': ADI_AGENT.phoneMobile,
  'sponsor.consents_to_electronic_communication': 'Yes',
  'sponsor.email': ADI_AGENT.email,
};

/**
 * "ASSISTANCE WITH THIS FORM" — ADI's own declaration as the assister
 * (Tzvi confirmed 2026-07-12: assistance blocks are ADI's to answer).
 *
 * `was_paid` (40SP, 1149) is seeded "Yes" — Eli confirmed via Tzvi 2026-07-12
 * that the assister is paid, and confirmed the title "Mr". On the 1006 the fee
 * question is gated behind is_registered_agent_or_lawyer == "No", which we
 * answer "Yes", so the Department never asks it there.
 *
 * Deliberately NOT set:
 * - `*title_other*`: gated behind title == "Other"; ADI's title is Mr.
 */
const ASSIST_1006: AgentPrefill = {
  'application.received_assistance': 'Yes',
  'application.assistant.title': ADI_AGENT.title,
  'application.assistant.family_name': ADI_AGENT.familyName,
  'application.assistant.given_names': ADI_AGENT.givenNames,
  'application.assistant.address': ADI_AGENT.postalAddress,
  'application.assistant.phone_business': ADI_AGENT.phoneBusiness,
  'application.assistant.phone_mobile': ADI_AGENT.phoneMobile,
  'application.assistant.is_registered_agent_or_lawyer': 'Yes',
};

const ASSIST_40SP: AgentPrefill = {
  'sponsor.received_assistance': 'Yes',
  'sponsor.assister.title': ADI_AGENT.title,
  'sponsor.assister.family_name': ADI_AGENT.familyName,
  'sponsor.assister.given_names': ADI_AGENT.givenNames,
  'sponsor.assister.address': ADI_AGENT.postalAddress,
  'sponsor.assister.phone_office': ADI_AGENT.phoneBusiness,
  'sponsor.assister.mobile': ADI_AGENT.phoneMobile,
  'sponsor.assister.is_registered_mara_agent': 'Yes',
  'sponsor.assister.in_australia': 'Yes',
  'sponsor.assister.was_paid': 'Yes',
};

const ASSIST_1149: AgentPrefill = {
  'sponsor.received_assistance': 'Yes',
  'sponsor.assister.is_registered_or_exempt': 'Yes',
  'sponsor.assister.was_paid': 'Yes',
  'sponsor.assister.title': ADI_AGENT.title,
  'sponsor.assister.family_name': ADI_AGENT.familyName,
  'sponsor.assister.given_names': ADI_AGENT.givenNames,
  'sponsor.assister.address': ADI_AGENT.postalAddress,
  'sponsor.assister.phone_office': ADI_AGENT.phoneBusiness,
  'sponsor.assister.mobile': ADI_AGENT.phoneMobile,
};

/** 820 / 309: the full block, including the recipient `type` radio. */
const PREFILL_47SP_STAGE1: AgentPrefill = {
  ...PREFILL_47SP_BASE,
  ...PREFILL_47SP_PHONES,
  'application.authorised_recipient.type': 'migration_agent',
};

/** stage-2: same, but the form has no `type` field. */
const PREFILL_47SP_STAGE2: AgentPrefill = {
  ...PREFILL_47SP_BASE,
  ...PREFILL_47SP_PHONES,
};

/** 300 (PMV): no `type`, and no phone fields either. */
const PREFILL_47SP_300: AgentPrefill = { ...PREFILL_47SP_BASE };

/**
 * Character battery on the 47SP forms. Unlike the 1419 (whose whole
 * `character_declarations` section is client_hidden), the partner forms hide
 * exactly ONE character question from the applicant:
 * `applicant.character.acquitted_by_insanity`, which is client_hidden AND
 * required, with no `appears_when` gate
 * (47SP_820_onshore_stage1.json:4116-4131; same block on 309, 300 and stage2 via
 * `shared_with`). Staff can still see and answer it; seeding means they do not
 * have to, and the lodged form is complete without a round trip.
 *
 * Eli's standing instruction (2026-07-23, via Tzvi): answer every character
 * question No, send the client the draft, and only flip a question to Yes (with
 * details and supporting documents) if the client flags it after reviewing.
 * Seed "No" so the lodged form is complete.
 *
 * `acquitted_by_insanity_details` is NOT seeded: its gate
 * (`... == "Yes"`) evaluates hidden while the answer is No.
 *
 * NOT included here, deliberately: the two `applicant.violence.*`
 * acknowledgements (the `violence_acknowledgements` subsection is client_hidden
 * at subsection level, 820:1226-1261, and both fields are required). One of them
 * is a CONSENT declaration, so ADI pre-answering it is Eli's call, not ours.
 * Still open with Eli as of 2026-07-24.
 */
const CHARACTER_47SP: AgentPrefill = {
  'applicant.character.acquitted_by_insanity': 'No',
};

/**
 * The 600 family (1418 / 1419) models it as a Yes/No plus a separate
 * `authorised_recipient.*` block, with title-case enum labels.
 *
 * `application.correspondence_recipient` is NOT an Immi field (the 600 capture has
 * exactly one correspondence question, F0070). It is a legacy portal field, so we
 * pin it to the value consistent with the rest: correspondence goes to the agent.
 */
const PREFILL_600: AgentPrefill = {
  'application.correspondence_recipient': 'Migration agent',
  'applicant.authorises_recipient': 'Yes',
  'authorised_recipient.type': 'Registered migration agent',
  'authorised_recipient.has_appointed_immigration_assistance': 'Yes',
  'authorised_recipient.marn': ADI_AGENT.marn,
  'authorised_recipient.relationship_to_applicant': 'Registered migration agent',
  'authorised_recipient.family_name': ADI_AGENT.familyName,
  'authorised_recipient.given_names': ADI_AGENT.givenNames,
  'authorised_recipient.organisation': ADI_AGENT.organisation,
  'authorised_recipient.address': ADI_AGENT.postalAddress,
  'authorised_recipient.phone_business': ADI_AGENT.phoneBusiness,
  'authorised_recipient.phone_mobile': ADI_AGENT.phoneMobile,
  'authorised_recipient.email': ADI_AGENT.email,
};

/** Sponsor forms carry only the "who receives written correspondence" radio. */
const PREFILL_SPONSOR: AgentPrefill = {
  'sponsor.written_correspondence_recipient': 'MigrationAgent',
};

/**
 * Per-form prefill, keyed by FORM_ID (the schema id from pickFormId) — NOT
 * intake_forms.form_code. form_code is the legacy v1 convention and is ambiguous:
 * 820, 309 and 300 all collapse to '47SP-applicant'.
 */
const BY_FORM_ID: Record<string, AgentPrefill> = {
  '47SP_820_onshore_stage1': { ...PREFILL_47SP_STAGE1, ...CONTACT_47SP_STAGE1, ...CHARACTER_47SP },
  '47SP_309_offshore_stage1': { ...PREFILL_47SP_STAGE1, ...CONTACT_47SP_STAGE1, ...CHARACTER_47SP },
  '47SP_300_PMV': { ...PREFILL_47SP_300, ...CONTACT_47SP_STAGE1, ...CHARACTER_47SP },
  '47SP_stage2': { ...PREFILL_47SP_STAGE2, ...CONTACT_47SP_STAGE2, ...CHARACTER_47SP },
  '1418_family_sponsored': { ...PREFILL_600, ...CONTACT_600_APPLICANT, ...CONTACT_1418_SPONSOR_BLOCK },
  // Pacific-Australia Card default (Wave 2, Jane item 12): the `pacific_australia_card`
  // subsection is client_hidden on 1419, so the applicant never sees Immi Q14. Seed the
  // common answer "No" so the lodged form is complete; staff flip it in the rare holder
  // case. Scoped to 1419 (NOT shared PREFILL_600) because 1419 is the only 600 form whose
  // pacific subsection this wave hides; seeding it on 1418, whose subsection stays
  // client-visible, would surface a prefilled field to that client.
  '1419_tourist': {
    ...PREFILL_600,
    ...CONTACT_600_APPLICANT,
    'applicant.is_pacific_australia_card_holder': 'No',
    // Group lodgement defaults to No (most 600s are single applicants); staff
    // flip to Yes when related applicants travel together (Tzvi 2026-07-23).
    'application.is_group_lodgement': 'No',
    // Special category of entry (Wave 3.3, Jane item 6): Immi's single Yes/No
    // gate + 3-way select replaces the three legacy always-visible yn limbs.
    // The legacy fields are kept but client_hidden (data-survival). Seed them
    // No so the staff/lodged form stays complete; the applicant answers only
    // the new gate. Scoped to 1419 (the form this wave rebuilds), NOT shared
    // PREFILL_600, because 1418 keeps the legacy fields client-visible.
    'applicant.is_foreign_govt_representative': 'No',
    'applicant.is_un_laissez_passer_holder': 'No',
    'applicant.is_exempt_group_member': 'No',
    // Health eligibility gates (Wave 3.7, Jane item 34): the classroom >3-months
    // and child-care-centre WORK questions are portal-only eligibility gates with
    // no Immi counterpart. Removed from the client view via client_hidden. They
    // are shared_with 1418 + 147, so kept (data-survival) and seeded No on the
    // 1419 only; the 1418/147 review passes decide those forms separately.
    'applicant.health.intends_childcare_work': 'No',
    'applicant.health.intends_classroom_3m': 'No',
    // Character battery is staff-only on the 1419 (character_declarations section
    // is client_hidden). Eli's standing instruction (2026-07-23, via Tzvi): answer
    // every character question No, send the client the draft, and only flip a
    // question to Yes (with details and supporting documents) if the client flags
    // it after reviewing the draft. Seed all 19 yn declarations "No" so the lodged
    // form is complete. `applicant.character.details` and `.supporting_documents`
    // are NOT seeded: their gates evaluate hidden/not-required while every answer
    // is No. Scoped to the 1419 (NOT shared PREFILL_600) because the 1418 keeps its
    // character battery client-visible.
    'applicant.character.charge_pending': 'No',
    'applicant.character.convicted': 'No',
    'applicant.character.dv_or_family_violence_offence': 'No',
    'applicant.character.dv_protective_order': 'No',
    'applicant.character.arrest_warrant_or_interpol': 'No',
    'applicant.character.child_sex_offence': 'No',
    'applicant.character.sex_offender_register': 'No',
    'applicant.character.acquitted_unsoundness': 'No',
    'applicant.character.unfit_to_plead': 'No',
    'applicant.character.national_security_risk': 'No',
    'applicant.character.war_crimes': 'No',
    'applicant.character.criminal_association': 'No',
    'applicant.character.violent_organisation_association': 'No',
    'applicant.character.military_or_police_service': 'No',
    'applicant.character.military_paramilitary_training': 'No',
    'applicant.character.people_smuggling': 'No',
    'applicant.character.removed_or_deported': 'No',
    'applicant.character.overstayed_visa': 'No',
    'applicant.character.outstanding_au_govt_debts': 'No',
  },
  '40SP_sponsor': { ...PREFILL_SPONSOR, ...CONTACT_40SP, ...ASSIST_40SP },
  '1149_family_visitor_sponsor': { ...PREFILL_SPONSOR, ...CONTACT_1149, ...ASSIST_1149 },
  // 1006 and 147 carry only the legacy correspondence_recipient radio, and their
  // option sets differ from the 600 family's, so they get their own values.
  '1006_bridging_visa_b': {
    'application.correspondence_recipient': 'Migration agent',
    ...CONTACT_1006,
    ...ASSIST_1006,
  },
  '147_nz_family': { 'application.correspondence_recipient': 'migration_agent' },
};

/** The ADI-as-agent answers for a form, or {} if the form has no recipient block. */
/**
 * Matter-variant presets (Tzvi ruling 2026-07-19). The relationship basis is
 * chosen by staff when the matter is created (married / de facto / engaged),
 * so the forms no longer ask the client for relationship status — it is
 * seeded from the variant and the field is client_hidden (staff can still see
 * and correct it). Likewise "related to the sponsor by blood, marriage or
 * adoption" is always No on ADI matters: seeded, client_hidden, staff-visible.
 * Blob-wins hydration means these seeds can never override a saved answer.
 */
const PARTNER_INITIAL_FORMS = new Set([
  '47SP_820_onshore_stage1',
  '47SP_309_offshore_stage1',
  '47SP_300_PMV',
]);

export function agentPrefillFor(formId: string, opts?: { variant?: string | null }): AgentPrefill {
  const base = BY_FORM_ID[formId] ?? {};
  if (!PARTNER_INITIAL_FORMS.has(formId)) return base;
  const extra: AgentPrefill = {
    'applicant.relationship.related_by_blood_marriage_adoption': 'No',
  };
  const status = statusForVariant(opts?.variant) ?? undefined;
  if (status) {
    extra['applicant.relationship.status'] = status;
    if (formId !== '47SP_300_PMV') extra['sponsor.relationship.status'] = status;
  }
  return { ...base, ...extra };
}
